/**
 * Draws a frame only when something changed: every `invalidate()` call
 * between two animation frames results in a single `render()`. Running
 * animations (e.g. pan inertia) step before the render and keep frames
 * coming only while they last.
 *
 * @param {() => void} render
 * @returns {{
 *   isFramePending: boolean,
 *   invalidate: () => void,
 *   animate: (step: (timestamp: number) => boolean) => () => void,
 *   destroy: () => void,
 * }}
 */
export const createRenderScheduler = (render) => {
  const animations = new Set();
  let frameId = null;

  const runFrame = (timestamp) => {
    frameId = null;
    animations.forEach((step) => {
      if (!step(timestamp)) animations.delete(step);
    });
    render();

    if (animations.size && frameId === null) frameId = requestAnimationFrame(runFrame);
  };

  return {
    /** A frame is requested and not drawn yet. */
    get isFramePending() {
      return frameId !== null;
    },

    invalidate: () => {
      if (frameId === null) frameId = requestAnimationFrame(runFrame);
    },

    /**
     * Calls `step(timestamp)` before every frame until it returns false.
     *
     * @returns {() => void} stops the animation
     */
    animate: (step) => {
      animations.add(step);

      if (frameId === null) frameId = requestAnimationFrame(runFrame);

      return () => animations.delete(step);
    },

    destroy: () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = null;
      animations.clear();
    },
  };
};
